import { JSONPath } from 'jsonpath-plus';
import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { getOptionByCustomField } from '@de/utils/getOptionByCustomField';
import {
  getOptionValueString,
  extractCustomFieldFromQueryParam,
} from '@de/utils/queryParamUtils';
import { DynamicOptions, SortConfig, Step } from '@de/types/types';
import { SearchParamsOptions } from '@de/utils/types';

export interface SortContext {
  userContext?: {
    country?: string;
    region?: string;
    isMobile?: boolean;
    searchPageType?: 'multi-lob' | 'lob-specific';
  };
  webContext?: Record<string, unknown>;
  flowAnswers?: Record<string, string>;
  listId?: string;
}

export interface FetchParams {
  dynamicContent: DynamicOptions;
  flowState: FlowState;
  steps: Step[];
  searchParamsOptions?: SearchParamsOptions;
  sortContext?: SortContext;
}

export const resolveQueryParams = (
  queryParams: string[],
  flowState: FlowState,
  steps: Step[],
  searchParamsOptions?: SearchParamsOptions,
): string | undefined => {
  if (!queryParams || queryParams.length === 0) return '';

  const resolvedParams = queryParams
    .map((param: string) => {
      const { key, customField } = extractCustomFieldFromQueryParam(param);
      const option = getOptionByCustomField(
        customField,
        steps,
        flowState,
        searchParamsOptions,
      );

      //if getOptionByCustomField doesn't find a matching option for the customField,
      // the query param will be excluded from the final URL.
      if (!option) {
        return null;
      }

      const value = getOptionValueString(option);

      if (value === null) {
        return null;
      }

      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .filter((param): param is string => param !== null);

  return resolvedParams.join('&');
};

/**
 * Returns only query params that can be resolved from flowState/steps.
 * Used for parameter fallback: try full set, then progressively fewer params.
 */
export const filterResolvableQueryParams = (
  queryParams: string[],
  steps: Step[],
  flowState: FlowState,
  searchParamsOptions?: SearchParamsOptions,
): string[] => {
  return queryParams.filter((param) => {
    const { customField } = extractCustomFieldFromQueryParam(param);
    const option = getOptionByCustomField(
      customField,
      steps,
      flowState,
      searchParamsOptions,
    );

    if (!option) return false;

    return getOptionValueString(option) !== null;
  });
};

export const buildUrlWithParams = (
  endpointUrl: string,
  queryParams: string[] | undefined,
  flowState: FlowState,
  steps: Step[],
  searchParamsOptions?: SearchParamsOptions,
): string => {
  const resolvedQueryParams = resolveQueryParams(
    queryParams || [],
    flowState,
    steps,
    searchParamsOptions,
  );
  if (!resolvedQueryParams) return endpointUrl;
  const separator = endpointUrl.includes('?') ? '&' : '?';
  return `${endpointUrl}${separator}${resolvedQueryParams}`;
};

export type JsonData = Record<string, unknown> | unknown[];

/**
 * Extracts values from JSON data using a JSONPath expression or a template
 * string containing one or more `{{JSONPath}}` placeholders mixed with static text.
 *
 * - Pure JSONPath (no `{{}}`)  → returned as-is from jsonpath-plus
 * - Template string            → each `{{expr}}` is resolved and interpolated
 *   e.g. `"{{$.data[*].weeksToComplete}} weeks to complete"` → `["6 weeks to complete", …]`
 *
 * @param path - JSONPath expression **or** template with `{{JSONPath}}` placeholders
 * @param data - JSON data to extract from
 * @returns Array of extracted values, or empty array if path is undefined
 */
export const extractJsonPathValues = (
  path: string | undefined,
  data: JsonData,
): unknown[] => {
  if (!path) return [];

  const templatePattern = /\{\{([^}]+)\}\}/g;
  const matches: RegExpExecArray[] = [];
  let match: RegExpExecArray | null;
  while ((match = templatePattern.exec(path)) !== null) {
    matches.push(match);
  }

  // No template expressions — treat the entire string as a pure JSONPath
  if (matches.length === 0) {
    return JSONPath({ path, json: data, wrap: true });
  }

  // Single template expression with no surrounding text — return raw values
  // so that original types (numbers, booleans, objects…) are preserved
  if (matches.length === 1 && matches[0][0] === path) {
    return JSONPath({ path: matches[0][1].trim(), json: data, wrap: true });
  }

  // Resolve every JSONPath expression found in the template
  const resolvedArrays = matches.map((match) =>
    JSONPath({ path: match[1].trim(), json: data, wrap: true }),
  );

  // Use the shortest resolved array to avoid index-out-of-bounds
  const length = Math.min(...resolvedArrays.map((arr) => arr.length));
  if (length === 0) return [];

  // For each index, replace every placeholder with the corresponding value.
  // Nullish/empty placeholders are substituted with "" and any HTML tags left
  // empty as a result (e.g. `<s></s>`) are stripped, so partial content like
  // `{{listPrice}}` still renders even when `{{strikethroughPrice}}` is absent.
  // The entire string is discarded only when *all* placeholders are nullish.
  return Array.from({ length }, (_, i) => {
    const allNullish = matches.every((_match, matchIndex) => {
      const val = resolvedArrays[matchIndex][i];
      return val == null || val === '';
    });
    if (allNullish) return null;

    let result = path;
    matches.forEach((match, matchIndex) => {
      const val = resolvedArrays[matchIndex][i];
      result = result.replace(
        match[0],
        val == null || val === '' ? '' : String(val),
      );
    });
    result = result.replace(/<(\w+)(?:\s[^>]*)?>[\s]*<\/\1>/g, '').trim();
    return result || null;
  });
};

export const resolveBody = (
  body: unknown,
  flowState: FlowState,
  steps: Step[],
  searchParamsOptions?: SearchParamsOptions,
): string | undefined => {
  if (!body) return undefined;

  const parsed =
    typeof body === 'string'
      ? JSON.parse(body.replace(/(?<!")\{\{([\w.]+)\}\}(?!")/g, '"{{$1}}"'))
      : body;

  /**
   * Recursively walks a parsed JSON value and interpolates `{{customField}}`
   * template tokens with the user's current flow selections.
   *
   * - **Pure token** (`"{{field}}"`) → replaced by the resolved option value;
   *   `drop: true` if the field is unresolved, so callers can omit the key entirely.
   * - **Mixed string** (`"Hello {{field}}"`) → tokens substituted in-place;
   *   unresolved tokens become empty strings, `drop` is always false.
   * - **Array** → each element resolved; items with `drop: true` are filtered out.
   * - **Object** → each value resolved; entries with `drop: true` are omitted.
   * - **Primitives** (number, boolean, null) → returned unchanged.
   */
  const resolveNode = (node: unknown): { value: unknown; drop: boolean } => {
    if (typeof node === 'string') {
      // Matches a string that is *entirely* a single template token, e.g. "{{some.field}}"
      // Capturing group 1 captures the field path (word chars and dots)
      const pureMatch = node.match(/^\{\{([\w.]+)\}\}$/);
      if (pureMatch) {
        const option = getOptionByCustomField(
          pureMatch[1],
          steps,
          flowState,
          searchParamsOptions,
        );

        if (!option) return { value: undefined, drop: true };
        const val = getOptionValueString(option);
        if (val === null || val === undefined)
          return { value: undefined, drop: true };
        return { value: val, drop: false };
      }

      // No template tokens at all — return the literal string as-is
      if (!/\{\{[\w.]+\}\}/.test(node)) {
        return { value: node, drop: false };
      }

      // Mixed string (e.g. "Hello {{name}}!") — replace every token in-place, missing values become ""
      const resolved = node.replace(
        /\{\{([\w.]+)\}\}/g,
        (_match, customField) => {
          const option = getOptionByCustomField(
            customField,
            steps,
            flowState,
            searchParamsOptions,
          );
          if (!option) return '';
          const val = getOptionValueString(option);
          return val ?? '';
        },
      );
      return { value: resolved, drop: false };
    }

    if (Array.isArray(node)) {
      const arr = node
        .map((item) => resolveNode(item))
        .filter(({ drop }) => !drop)
        .map(({ value }) => value);
      return { value: arr, drop: false };
    }

    if (typeof node === 'object' && node !== null) {
      const obj: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(
        node as Record<string, unknown>,
      )) {
        const { value, drop } = resolveNode(val);
        if (!drop) obj[key] = value;
      }
      return { value: obj, drop: false };
    }

    return { value: node, drop: false };
  };

  const { value } = resolveNode(parsed);
  return JSON.stringify(value);
};

const applySortToUrl = (
  url: string,
  dynamicContent: DynamicOptions,
  sortContext: SortContext,
): string => {
  const urlObj = new URL(url, 'http://placeholder');

  if (!urlObj.searchParams.has('sortBy')) {
    urlObj.searchParams.set('sortBy', 'ml-fyp');
  }

  if (!urlObj.searchParams.has('limit') && dynamicContent.limit != null) {
    urlObj.searchParams.set('limit', String(dynamicContent.limit));
  }

  if (!urlObj.searchParams.has('sortMaxResults')) {
    const urlLimit = urlObj.searchParams.get('limit');
    const effectiveLimit =
      urlLimit ??
      (dynamicContent.limit != null ? String(dynamicContent.limit) : null);
    if (effectiveLimit != null) {
      urlObj.searchParams.set('sortMaxResults', String(effectiveLimit));
    }
  }

  if (sortContext.listId) {
    urlObj.searchParams.set('sortListId', sortContext.listId);
  }

  return urlObj.toString().replace('http://placeholder', '');
};

const applySortToBody = (
  resolvedBody: string,
  sortContext: SortContext,
  sortConfig: SortConfig,
): string => {
  const bodyObj = JSON.parse(resolvedBody);

  bodyObj.sortUserContext = {
    ...bodyObj.sortUserContext,
    country: sortContext.userContext?.country || '',
    region: sortContext.userContext?.region || '',
    isMobile: sortContext.userContext?.isMobile || false,
    ...(sortContext.userContext?.searchPageType && {
      searchPageType: sortContext.userContext.searchPageType,
    }),
  };

  if (sortConfig.weaverModel && !bodyObj.weaverModel) {
    bodyObj.weaverModel = sortConfig.weaverModel;
  }

  if (sortContext.webContext && !bodyObj.sortWebContext) {
    bodyObj.sortWebContext = sortContext.webContext;
  }
  if (
    sortConfig.weaverModel === 'fyp' &&
    sortContext.flowAnswers &&
    !bodyObj.flowAnswers
  ) {
    bodyObj.flowAnswers = sortContext.flowAnswers;
  }

  return JSON.stringify(bodyObj);
};

const applyGeoToUrl = (url: string, country: string): string => {
  const urlObj = new URL(url, 'http://placeholder');
  if (!urlObj.searchParams.has('country')) {
    urlObj.searchParams.set('country', country);
  }
  return urlObj.toString().replace('http://placeholder', '');
};

export const fetchWithDynamicParams = async <T>(
  params: FetchParams,
  errorContext: string,
  mapResponse: (data: JsonData, dynamicOptions: DynamicOptions) => T,
): Promise<T> => {
  const { dynamicContent, flowState, steps, searchParamsOptions, sortContext } =
    params;
  const { endpointUrl, method, body, queryParams, sortConfig } = dynamicContent;

  if (!endpointUrl) {
    throw new Error('Endpoint URL is required');
  }

  let url = buildUrlWithParams(
    endpointUrl,
    queryParams,
    flowState,
    steps,
    searchParamsOptions,
  );

  const isSortEnabled = sortConfig?.enabled && sortContext;

  if (isSortEnabled) {
    url = applySortToUrl(url, dynamicContent, sortContext);
  }

  let resolvedBody =
    method === 'POST'
      ? resolveBody(body, flowState, steps, searchParamsOptions)
      : undefined;

  if (isSortEnabled && resolvedBody) {
    resolvedBody = applySortToBody(resolvedBody, sortContext, sortConfig!);
  }

  const geoCountry = sortContext?.userContext?.country;
  if (geoCountry) {
    url = applyGeoToUrl(url, geoCountry);
  }

  try {
    const response = await fetch(url, {
      method: method || 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      body: resolvedBody,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch ${errorContext}: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return mapResponse(data, dynamicContent);
  } catch (error) {
    console.error(`Error fetching ${errorContext}:`, error);
    throw new Error(`Failed to fetch ${errorContext}: ${error}`);
  }
};
